import type { FC, ReactNode } from 'react'
import type { ArcId } from '@/types/content'
import type { ArcChapter } from '@/components/story/arc-chapters'
import type { TimelineEntry } from '@/components/story/event-timeline'

import { ArrowUp, ListOrdered, Milestone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLocale } from '@/hooks/use-locale'
import ArcArt from '@/components/story/arc-art'
import ArcChapters from '@/components/story/arc-chapters'
import { ARC_NAV_ID } from '@/components/story/arc-jump-nav'
import EventTimeline from '@/components/story/event-timeline'

export type StoryArcEvent = {
    parva: number
    title: string
    body: string
}

export type StoryArc = {
    id: ArcId
    number: number
    range: [number, number]
    title: string
    intro: string
    image: string | null
    imageAlt: string
    events: StoryArcEvent[]
    chapters: ArcChapter[]
}

interface ArcSectionProps {
    arc: StoryArc
    tinted?: boolean
    divided?: boolean
}

const ArcSection: FC<ArcSectionProps> = ({
    arc,
    tinted = false,
    divided = false
}): ReactNode => {
    const { locale, t } = useLocale()
    const latin = locale === 'en'
    const titleId = `${arc.id}-title`
    const timelineTitleId = `${arc.id}-timeline-title`
    const hasEvents = arc.events.length > 0
    const available = new Set(arc.chapters.map(chapter => chapter.number))
    const TimelineIcon = hasEvents ? Milestone : ListOrdered

    const range = t.parvaRange
        .replace('{from}', String(arc.range[0]))
        .replace('{to}', String(arc.range[1]))

    const entries: TimelineEntry[] = hasEvents
        ? arc.events.map((event, index) => ({
            id: `${arc.id}-event-${index}`,
            parva: event.parva,
            title: event.title,
            body: event.body,
            linked: available.has(event.parva)
        }))
        : arc.chapters.map(chapter => ({
            id: `${arc.id}-parva-${chapter.number}`,
            parva: chapter.number,
            title: chapter.title,
            body: chapter.headline,
            linked: true
        }))

    return (
        <section
            id={arc.id}
            aria-labelledby={titleId}
            className={cn('relative scroll-mt-16', tinted && 'bg-secondary/60')}
        >
            {divided && <div aria-hidden='true' className='gold-rule absolute inset-x-0 top-0' />}

            <div className='mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24'>
                <ArcArt
                    image={arc.image}
                    alt={arc.imageAlt}
                    number={arc.number}
                    sizes='(min-width: 1280px) 1232px, (min-width: 640px) calc(100vw - 3rem), calc(100vw - 2rem)'
                    className='aspect-[16/10] shadow-[0_28px_60px_-36px_rgb(40_20_60/0.65)] md:aspect-[21/9]'
                />

                <div className='mt-10 grid gap-12 sm:mt-12 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16'>
                    <div className='min-w-0 lg:top-24 lg:self-start lg:[@media(min-height:48rem)]:sticky'>
                        <p className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 font-medium', latin ? 'text-xs uppercase tracking-[0.18em]' : 'text-sm')}>
                            <span className='text-primary'>
                                {t.arcNumber.replace('{number}', String(arc.number))}
                            </span>

                            {' '}
                            <span aria-hidden='true' className='size-1 rounded-full bg-gold' />
                            {' '}

                            <span className='text-muted-foreground'>
                                {range}
                            </span>
                        </p>

                        <h2 id={titleId} className={cn('mt-4 font-heading text-3xl font-semibold text-foreground sm:text-4xl lg:text-5xl', latin ? 'leading-[1.08]' : 'leading-snug')}>
                            {arc.title}
                        </h2>

                        <div aria-hidden='true' className='mt-6 h-px w-32 bg-linear-to-r from-gold to-transparent' />

                        <p
                            className={cn(
                                'mt-6 text-base text-foreground/85 sm:text-lg',
                                latin
                                    ? 'leading-relaxed first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:font-heading first-letter:text-[3.75rem] first-letter:font-semibold first-letter:leading-[0.8] first-letter:text-primary'
                                    : 'leading-[1.85]'
                            )}
                        >
                            {arc.intro}
                        </p>
                    </div>

                    <div className='min-w-0'>
                        <h3 id={timelineTitleId} className='flex items-center gap-2.5 font-heading text-2xl font-semibold text-foreground sm:text-3xl'>
                            <TimelineIcon className='size-5 shrink-0 text-primary' aria-hidden='true' />
                            {hasEvents ? t.storyKeyEvents : t.storyMainPoints}
                        </h3>

                        <div aria-hidden='true' className='gold-rule mt-4' />

                        <EventTimeline entries={entries} labelledBy={timelineTitleId} className='mt-8' />

                        <ArcChapters
                            chapters={arc.chapters}
                            titleId={`${arc.id}-chapters-title`}
                            className={entries.length > 0 ? 'mt-12' : 'mt-8'}
                        />
                    </div>
                </div>

                <div className='mt-12 flex justify-end'>
                    <a
                        href={`#${ARC_NAV_ID}`}
                        className='inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold motion-reduce:transition-none'
                    >
                        <ArrowUp className='size-4' aria-hidden='true' />
                        {t.storyBackToArcs}
                    </a>
                </div>
            </div>
        </section>
    )
}

export default ArcSection
